// AddStops.jsx
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Card, Container, Row, Col, Form, Button } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

dayjs.extend(customParseFormat);


const _coordSanitize = (val) =>
  String(val ?? '')
    .replace(/[^0-9.\-]/g, '')
    .replace(/(?!^)-/g, '')
    .replace(/(\..*)\./g, '$1');

const _fmt6 = (v) => (v === '' || v == null ? '' : Number.parseFloat(v).toFixed(6));
const _clamp = (num, min, max) => Math.min(Math.max(num, min), max);
const toMinutes = (t) => (dayjs.isDayjs(t) && t.isValid() ? t.hour() * 60 + t.minute() : null);
const isAlnumSpace = (s) => /^[A-Za-z ]+$/.test(s);
const handleStopNameKeyDown = (e) => {
  const k = e.key;
  const ctrlCmd = e.ctrlKey || e.metaKey;
  // allow letters only (no digits)
  if (/^[A-Za-z]$/.test(k)) return;
  // allow space but not as first character
  if (k === ' ' && e.currentTarget.selectionStart > 0) return;
  const nav = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'];
  if (nav.includes(k)) return;
  if (ctrlCmd && ['a', 'c', 'v', 'x', 'z', 'y'].includes(k.toLowerCase())) return;
  e.preventDefault();
};
const handleStopNamePaste = (e) => {
  const t = (e.clipboardData || window.clipboardData).getData('text');
  if (!isAlnumSpace(t)) {
    e.preventDefault();
    toast.error('Only letters and spaces are allowed in Stop Name.');
  }
};

const AddStops = () => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData || '{}');
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const routeLocation = useLocation();

  const [form, setForm] = useState({
    route_id: '',
    stop_id: '',
    stop_name: '',
    stop_sequence: '',
    pickup_time: null,
    drop_time: null,
    address: '',
    latitude: '',
    longitude: ''
  });

  const parseStopTime = (v) => {
    if (!v) return null;
    const parsed = dayjs(v, ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm', 'HH:mm:ss'], true);
    return parsed.isValid() ? parsed : null;
  };

  const fetchRoutes = async () => {
    const resp = await axios.get(`${baseUrl}/routes/${userObj.school_id}`, { params: { action: 'READ' } });
    setRoutes(resp.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        await fetchRoutes();
      } catch {
        toast.error('Failed to load routes');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleLatChange = (e) => {
    const raw = _coordSanitize(e.target.value);
    setForm((p) => ({ ...p, latitude: raw }));
  };
  const handleLngChange = (e) => {
    const raw = _coordSanitize(e.target.value);
    setForm((p) => ({ ...p, longitude: raw }));
  };
  const handleLatBlur = () => {
    if (form.latitude === '') return;
    const n = Number(form.latitude);
    if (Number.isNaN(n)) return;
    const clamped = _clamp(n, -90, 90);
    setForm((p) => ({ ...p, latitude: _fmt6(clamped) }));
  };
  const handleLngBlur = () => {
    if (form.longitude === '') return;
    const n = Number(form.longitude);
    if (Number.isNaN(n)) return;
    const clamped = _clamp(n, -180, 180);
    setForm((p) => ({ ...p, longitude: _fmt6(clamped) }));
  };

  useEffect(() => {
    const rd = routeLocation.state?.routesData;
    if (!rd) return;
    setEditId(rd.stop_id ?? null);
    setForm({
      route_id: rd.route_id != null ? String(rd.route_id) : '',
      stop_id: rd.stop_id != null ? String(rd.stop_id) : '',
      stop_name: rd.stop_name ?? '',
      stop_sequence: rd.stop_sequence != null ? String(rd.stop_sequence) : '',
      pickup_time: parseStopTime(rd.pickup_time),
      drop_time: parseStopTime(rd.drop_time),
      address: rd.address || '',
      latitude: rd.latitude != null ? String(rd.latitude) : '',
      longitude: rd.longitude != null ? String(rd.longitude) : ''
    });
    navigate('.', { replace: true, state: {} });
  }, [routeLocation, navigate]);

  useEffect(() => {
    const rd = routeLocation.state?.routesData;
    if (!rd) return;
    if (editId === null || form.route_id || !routes.length || !rd.route_name) return;
    const match = routes.find(
      (r) => (r.route_name || '').trim().toLowerCase() === rd.route_name.trim().toLowerCase()
    );
    if (match) setForm((p) => ({ ...p, route_id: String(match.route_id) }));
  }, [routes, routeLocation, form.route_id, editId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = (form.stop_name || '').trim();
    if (!name || !isAlnumSpace(name)) {
      toast.error('Stop Name must contain only letters, numbers and spaces.');
      return;
    }

    if (form.latitude !== '') {
      const n = Number(form.latitude);
      if (!Number.isFinite(n) || n < -90 || n > 90) {
        toast.error('Latitude must be between -90 and 90');
        return;
      }
    }
    if (form.longitude !== '') {
      const n = Number(form.longitude);
      if (!Number.isFinite(n) || n < -180 || n > 180) {
        toast.error('Longitude must be between -180 and 180');
        return;
      }
    }

    const pMin = toMinutes(form.pickup_time);
    const dMin = toMinutes(form.drop_time);
    if (pMin == null || dMin == null) {
      toast.error('Pickup and Drop Time are required');
      return;
    }
    if (pMin === dMin) {
      toast.error('Pickup and drop times cannot be the same.');
      return;
    }
    if (pMin > dMin) {
      toast.error('Pickup time cannot be later than drop time');
      return;
    }

    const payload = {
      route_id: form.route_id !== '' ? Number(form.route_id) : null,
      stop_name: name,
      stop_sequence: form.stop_sequence !== '' ? Number(form.stop_sequence) : null,
      pickup_time: dayjs.isDayjs(form.pickup_time) ? form.pickup_time.format('HH:mm') : null,
      drop_time: dayjs.isDayjs(form.drop_time) ? form.drop_time.format('HH:mm') : null,
      school_id: userObj.school_id,
      action: editId !== null ? 'UPDATE' : 'CREATE',
      created_by: userObj.user_name,
      updated_by: userObj.user_name,
      address: form.address || '',
      latitude: form.latitude !== '' ? Number(parseFloat(form.latitude).toFixed(6)) : null,
      longitude: form.longitude !== '' ? Number(parseFloat(form.longitude).toFixed(6)) : null,
      ...(editId !== null ? { stop_id: Number(editId) } : {})
    };

    try {
      await axios.post(`${baseUrl}/stops`, payload, { headers: { 'Content-Type': 'application/json' } });
      toast.success(editId !== null ? 'Record Updated Successfully' : 'Record Added Successfully');
      setForm({
        route_id: '',
        stop_id: '',
        stop_name: '',
        stop_sequence: '',
        pickup_time: null,
        drop_time: null,
        address: '',
        latitude: '',
        longitude: ''
      });
      setEditId(null);
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const msg = data?.detail?.[0]?.msg || data?.detail || data?.error || data?.message || 'Unexpected error occurred';
      if (status === 409) toast.error('Record Already Exists');
      else toast.error(String(msg));
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">Stop Details</h6>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Route Name <span className="requiredStar">*</span></Form.Label>
                      <Form.Select
                        required
                        id="route_id"
                        value={form.route_id}
                        onChange={handleInputChange}
                        disabled={isLoading || routes.length === 0}
                      >
                        <option value="">Select Route Name</option>
                        {(routes || []).map((r) => (
                          <option key={r.route_id} value={r.route_id}>
                            {r.route_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Stop Name <span className="requiredStar">*</span></Form.Label>
                      <Form.Control
                        required
                        type="text"
                        id="stop_name"
                        value={form.stop_name}
                        placeholder="Enter Stop Name"
                        maxLength={40}
                        onChange={handleInputChange}
                        onKeyDown={handleStopNameKeyDown}
                        onPaste={handleStopNamePaste}
                        pattern="[A-Za-z ]+"
                        title="Use letters and spaces only"
                        onKeyUp={(e) => {
                          const v = e.currentTarget.value.replace(/\s{2,}/g, ' ');
                          if (v !== form.stop_name) setForm((p) => ({ ...p, stop_name: v }));
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Stop Sequence <span className="requiredStar">*</span></Form.Label>
                      <Form.Control
                        required
                        type="text"
                        id="stop_sequence"
                        value={form.stop_sequence}
                        maxLength={2}
                        placeholder="Enter Stop Sequence"
                        inputMode="numeric"
                        onChange={(e) => {
                          const filtered = e.target.value.replace(/\D/g, '');
                          setForm((p) => ({ ...p, stop_sequence: filtered }));
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Pickup Time <span className="requiredStar">*</span></Form.Label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          ampm
                          minutesStep={5}
                          value={dayjs.isDayjs(form.pickup_time) ? form.pickup_time : null}
                          onChange={(val) => setForm((p) => ({ ...p, pickup_time: val }))}
                          renderInput={(params) => <TextField {...params} required fullWidth size="small" />}
                        />
                      </LocalizationProvider>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Drop Time <span className="requiredStar">*</span></Form.Label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          ampm
                          minutesStep={5}
                          value={dayjs.isDayjs(form.drop_time) ? form.drop_time : null}
                          onChange={(val) => setForm((p) => ({ ...p, drop_time: val }))}
                          renderInput={(params) => <TextField {...params} required fullWidth size="small" />}
                        />
                      </LocalizationProvider>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={12} lg={8} xxl={6}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        as="textarea"
                        type="text"
                        id="address"
                        value={form.address}
                        placeholder="Enter Address"
                        onChange={handleInputChange}
                        maxLength={250}
                        rows="3"
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Latitude</Form.Label>
                      <Form.Control
                        type="text"
                        id="latitude"
                        value={form.latitude}
                        placeholder="Enter Coordinates"
                        inputMode="decimal"
                        onChange={handleLatChange}
                        onBlur={handleLatBlur}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Longitude</Form.Label>
                      <Form.Control
                        type="text"
                        id="longitude"
                        value={form.longitude}
                        placeholder="Enter Coordinates"
                        inputMode="decimal"
                        onChange={handleLngChange}
                        onBlur={handleLngBlur}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-between mt-3">
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-info clearBtn"
                    onClick={() =>
                      setForm({
                        route_id: '',
                        stop_id: '',
                        stop_name: '',
                        stop_sequence: '',
                        pickup_time: null,
                        drop_time: null,
                        address: '',
                        latitude: '',
                        longitude: ''
                      })
                    }
                  >
                    Reset
                  </Button>
                </div>
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-danger secondaryBtn me-2"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" className="btn-success primaryBtn">
                    Submit
                  </Button>
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AddStops;
